import { useDateNow, useReduxState } from 'hooks';
import { getMessageWithPlaceholders } from 'shared-locales';
import { breakpoints } from 'styles';
import { useApiData } from 'lib/api';
import { getFormattedDate } from 'lib/common';
import { CalendarEventDateString } from 'components/calendar';
import {
  Avatar,
  Badge,
  Link,
  LoadingIndicator,
  ProductLinkWithIcon,
  ProgressBar
} from 'components/common';
import { Icon } from 'components/common/icons';
import { ParticipantsStats } from 'components/common/stats';
import { PathStepItemWarning } from '.';

import type {
  GetPathStepsMinimalRes,
  GetPathStepsRes
} from 'shared-types/responses';

type Props = {
  stepLinkHref?: string;
} & (
  | {
      detailedView?: false;
      step: GetPathStepsMinimalRes['steps'][0];
    }
  | {
      detailedView: true;
      step: GetPathStepsRes['steps'][0];
    }
);

const PathStepSummary = ({
  detailedView = false,
  step,
  stepLinkHref
}: Props) => {
  const { dateNow } = useDateNow();

  const {
    messages,
    pageState: { path },
    theme
  } = useReduxState();

  const { data, loading } = useApiData<GetPathStepsRes>({
    url: `/admin/paths/${path.id}/steps?stepId=${step.id}`,
    onlyIf: Boolean(step) && !detailedView
  });

  return !step ? null : (
    <div className="content">
      <div className="step">
        <div className="step-content">
          <div className="number">
            <Avatar backgroundColor={theme.lightGreyDark} fontSize="1.4rem">
              {step.number}
            </Avatar>
          </div>

          <div className="title">
            {step.items.length > 1 ? (
              <div className="alternatives">
                <div className="alternatives-icon">
                  <Icon icon="share-alt" />
                </div>
                {detailedView ? (
                  getMessageWithPlaceholders({
                    message: messages.commonNumberOfAlternatives,
                    placeholders: { numberOfAlternatives: step.items.length }
                  })
                ) : (
                  <Link
                    href={stepLinkHref}
                    text={getMessageWithPlaceholders({
                      message: messages.commonNumberOfAlternatives,
                      placeholders: { numberOfAlternatives: step.items.length }
                    })}
                  />
                )}
              </div>
            ) : (
              <div className="step-item-content">
                <div className="title-and-warning">
                  <ProductLinkWithIcon
                    accessType={step.items[0]?.accessType}
                    achievement={step.items[0]?.achievement}
                    calendarEvent={step.items[0]?.event}
                    course={step.items[0]?.course}
                    linkHref={stepLinkHref}
                    titleStyle={{ fontWeight: 600 }}
                  />
                  {step.items[0].course && !step.items[0].course.startDate ? (
                    <PathStepItemWarning
                      type="unpublished-course"
                      style={{ top: 4 }}
                    />
                  ) : step.items[0].course?.userAccess === 'guests' ? (
                    <PathStepItemWarning
                      type="guests-only-course"
                      style={{ top: 4 }}
                    />
                  ) : step.items[0].event?.endDate &&
                    dateNow > Date.parse(step.items[0].event?.endDate) ? (
                    <PathStepItemWarning
                      type="event-ended"
                      style={{ top: 4 }}
                    />
                  ) : null}
                </div>
                {step.items[0]?.event && (
                  <div
                    className="event-date"
                    style={{
                      color:
                        new Date(step.items[0]?.event?.endDate) >= new Date()
                          ? theme.textColorLight
                          : theme.dangerColor
                    }}
                  >
                    <CalendarEventDateString
                      startDate={step.items[0]?.event?.startDate}
                      endDate={step.items[0]?.event?.endDate}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {detailedView ? (
          <div className="stats">
            <ParticipantsStats
              completionEnabled={true}
              emptyStyle={{ top: -1 }}
              progressData={(step as GetPathStepsRes['steps'][0]).progressData}
            />
          </div>
        ) : loading ? (
          <div className="stats" style={{ marginRight: 50 }}>
            <LoadingIndicator padding={5} size={20} />
          </div>
        ) : data?.steps?.[0] ? (
          <div className="stats">
            <ParticipantsStats
              completionEnabled={true}
              emptyStyle={{ top: 3 }}
              progressData={data?.steps[0].progressData}
            />
          </div>
        ) : null}
      </div>

      <div
        className="info"
        style={{ marginTop: step.items?.length > 1 ? 3 : 6 }}
      >
        <div className="mandatory">
          <Badge
            size="small"
            text={
              step.isMandatory
                ? messages.commonMandatory
                : messages.commonOptional
            }
          />
        </div>

        <div className="schedule">
          <Badge
            icon={
              (step.deadlineType === 'date' && step.deadlineDate) ||
              (step.deadlineType === 'duration' &&
                step.deadlineDurationValue &&
                step.deadlineDurationUnit)
                ? ['far', 'clock']
                : null
            }
            size="small"
            text={
              step.deadlineType === 'date' && step.deadlineDate
                ? `${messages.courseTargetSchedule}: ${getFormattedDate({
                    date: step.deadlineDate
                  })}`
                : step.deadlineType === 'duration' &&
                  step.deadlineDurationValue &&
                  step.deadlineDurationUnit
                ? `${messages.courseTargetSchedule}: ` +
                  `${step.deadlineDurationValue} ` +
                  `${
                    {
                      days: messages.commonDays,
                      weeks: messages.commonWeeks,
                      months: messages.commonMonths
                    }[step.deadlineDurationUnit]
                  }`
                : messages.courseTargetScheduleNone
            }
          />
        </div>
      </div>

      {step.items?.length > 1 ? (
        <div className="step-items">
          {step.items.map(
            (
              item:
                | GetPathStepsMinimalRes['steps'][0]['items'][0]
                | GetPathStepsRes['steps'][0]['items'][0]
            ) => {
              let completed = 0;
              let participants = 0;
              let started = 0;
              if ('progressData' in item && item.progressData) {
                completed = item.progressData.completed;
                participants = item.progressData.participants;
                started = item.progressData.started;
              }

              return (
                <div className="step-item" key={`${step.id}-${item.id}`}>
                  <div className="step-item-content">
                    <div className="title-and-warning">
                      <ProductLinkWithIcon
                        accessType={item.accessType}
                        achievement={item.achievement}
                        calendarEvent={item.event}
                        course={item.course}
                        disableLink={!detailedView}
                        titleStyle={{ fontWeight: 600 }}
                      />

                      {item.course && !item.course.startDate ? (
                        <PathStepItemWarning
                          type="unpublished-course"
                          style={{ top: 4 }}
                        />
                      ) : item.course?.userAccess === 'guests' ? (
                        <PathStepItemWarning
                          type="guests-only-course"
                          style={{ top: 4 }}
                        />
                      ) : item.event?.endDate &&
                        dateNow > Date.parse(item.event?.endDate) ? (
                        <PathStepItemWarning
                          type="event-ended"
                          style={{ top: 4 }}
                        />
                      ) : null}
                    </div>
                    {item.event && (
                      <div
                        className="event-date"
                        style={{
                          color:
                            new Date(step.items[0]?.event?.endDate) >=
                            new Date()
                              ? theme.textColorLight
                              : theme.dangerColor
                        }}
                      >
                        <CalendarEventDateString
                          startDate={item.event.startDate}
                          endDate={item.event.endDate}
                        />
                      </div>
                    )}
                  </div>

                  {detailedView && step.items?.length > 1 ? (
                    <div className="step-item-progress">
                      {participants ? (
                        <ProgressBar
                          labels={[
                            `${completed} ${messages.tasksUsersCompletedSingular}`,
                            `${started - completed} ` +
                              messages.commonInProgress
                          ]}
                          labelStyle={{
                            color: theme.textColorLight,
                            marginBottom: 3
                          }}
                          progresses={[
                            {
                              color: theme.successStandaloneColor,
                              progress: completed / participants
                            },
                            {
                              color: 'rgba(0, 0, 0, 0.4)',
                              progress: started / participants
                            }
                          ]}
                        />
                      ) : (
                        <div className="step-item-progress-empty">
                          {messages.commonNoUsers}
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              );
            }
          )}
        </div>
      ) : null}

      <style jsx>{`
        .content {
          display: flex;
          flex-direction: column;
        }
        .step {
          align-items: flex-start;
          display: flex;
          flex-direction: column;
          flex-grow: 1;
        }
        .number {
          margin-right: 20px;
        }
        .step-content {
          display: flex;
          flex-grow: 1;
        }
        .alternatives {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          font-weight: 700;
          margin-top: 4px;
        }
        .alternatives-icon {
          display: inline-block;
          margin-right: 8px;
          text-align: center;
          width: 24px;
        }
        .info {
          display: flex;
          font-size: 1.4rem;
          font-weight: 400;
          gap: 20px;
          padding-left: 46px;
        }
        .info-icon {
          color: ${theme.textColorLight};
          display: inline-block;
          font-size: 1.3rem;
          margin-right: 5px;
        }
        .step-items {
          display: flex;
          flex-direction: column;
          font-size: 1.6rem;
          font-weight: 600;
          gap: 25px 15px;
          margin: 25px 10px 0 48px;
        }
        .step-item {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          gap: 10px;
        }
        .step-item-content {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }
        .title-and-warning {
          display: flex;
          gap: 7px;
        }
        .event-date {
          font-size: 1.2rem;
          font-weight: 600;
          margin-top: -4px;
          padding-left: 32px;
        }
        .step-item-progress {
          display: inline-flex;
          position: relative;
          top: 2px;
        }
        .step-item-progress-empty {
          color: ${theme.textColorLighter};
          font-size: 1.3rem;
          font-weight: 600;
          line-height: 1;
          margin-top: 5px;
        }
        .stats {
          margin: 10px 0 10px 50px;
        }
        @media (min-width: ${breakpoints.large}px) {
          .step {
            flex-direction: row;
          }
          .stats {
            margin: 3px 10px 0 0;
          }
          .step-items {
            gap: 15px;
            margin-top: 20px;
          }
          .step-item {
            flex-direction: row;
          }
          .step-item-content {
            flex-direction: column;
          }
          .event-date {
            padding-bottom: 5px;
          }
        }
      `}</style>
    </div>
  );
};

export default PathStepSummary;
